import React, { useState } from 'react';
import Filters from '../../../components/Filter';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { DeacParamList } from '../../../typings/Params';
import { useAuth } from '../../../context/Auth';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import UserDropdown from '../../../components/deac/UserDropdown';
import { VacancyPresenceStatus } from '../../../services/central-api/deac';
import WorkplacesDropdown from '../../../components/deac/WorkplacesDropdown';
import ActingBodiesCommandDropdown from '../../../components/deac/ActingBodiesCommandDropdown';

interface Props {
    filters: DeacParamList['PresenceControl'];
    setFilters: React.Dispatch<React.SetStateAction<DeacParamList['PresenceControl']>>;
    textFilter: string;
    setTextFilter: (value: string) => void;
    type: VacancyPresenceStatus;
    onStatusChange?: (isOpen: boolean) => void;
}

function Filter({ filters, setFilters, textFilter, setTextFilter, type, onStatusChange }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<DeacParamList, 'PresenceControl'>>>();
    const { user: authenticatedUser } = useAuth();
    const { styles } = useStyles(styleSheet);

    const [workplaceId, setWorkplaceId] = useState<number>(filters.workplaceId ?? 0);
    const [actingBodyId, setActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [actingBodyUnitId, setActingBodyUnitId] = useState<number>(filters.actingBodyUnitId ?? 0);
    const [vacancyActingBodyId, setVacancyActingBodyId] = useState<number>(filters.vacancyActingBodyId ?? 0);
    const [vacancyActingBodyCommandId, setVacancyActingBodyCommandId] = useState<number>(filters.vacancyActingBodyId ?? 0);
    const [vacancyActingBodyUnitId, setVacancyActingBodyUnitId] = useState<number>(filters.vacancyActingBodyUnitId ?? 0);
    const [registeredBy, setRegisteredBy] = useState<number>(filters.registeredBy ?? 0);
    const [changedBy, setChangedBy] = useState<number>(filters.changedBy ?? 0);

    const handleCleanFilter = () => {
        setTextFilter('');
        setActingBodyId(0);
        setActingBodyUnitId(0);
        setRegisteredBy(0);
        setChangedBy(0);
        setWorkplaceId(0);
        setVacancyActingBodyId(0);
        setVacancyActingBodyUnitId(0);
        setVacancyActingBodyCommandId(0);
    };

    const handleFilter = () => {
        navigation.setParams({
            ...filters,
            page: 1,
            textFilter: textFilter !== '' ? textFilter : undefined,
            actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined,
            workplaceId: workplaceId !== 0 ? workplaceId : undefined,
            actingBodyUnitId: actingBodyUnitId !== 0 ? actingBodyUnitId : undefined,
            vacancyActingBodyId: vacancyActingBodyId !== 0 ? vacancyActingBodyId : undefined,
            vacancyActingBodyUnitId: vacancyActingBodyUnitId !== 0 ? vacancyActingBodyUnitId : undefined,
            vacancyActingBodyCommandId: vacancyActingBodyCommandId !== 0 ? vacancyActingBodyCommandId : undefined,
            registeredBy: registeredBy !== 0 ? registeredBy : undefined,
            changedBy: changedBy !== 0 ? changedBy : undefined,
        });

        setFilters({
            ...filters,
            page: 0,
            textFilter: textFilter !== '' ? textFilter : undefined,
            actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined,
            workplaceId: workplaceId !== 0 ? workplaceId : undefined,
            actingBodyUnitId: actingBodyUnitId !== 0 ? actingBodyUnitId : undefined,
            vacancyActingBodyId: vacancyActingBodyId !== 0 ? vacancyActingBodyId : undefined,
            vacancyActingBodyUnitId: vacancyActingBodyUnitId !== 0 ? vacancyActingBodyUnitId : undefined,
            vacancyActingBodyCommandId: vacancyActingBodyCommandId !== 0 ? vacancyActingBodyCommandId : undefined,
            registeredBy: registeredBy !== 0 ? registeredBy : undefined,
            changedBy: changedBy !== 0 ? changedBy : undefined,
        });
    };

    const getActingBodyId = () => {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_presence_control) {
            return actingBodyId;
        }

        return authenticatedUser.actingBodyUnit.actingBodyId;
    };

    const getVacancyActingBodyId = () => {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_presence_control) {
            return vacancyActingBodyId;
        }

        return authenticatedUser.actingBodyUnit.actingBodyId;
    };

    const getVacancyActingBodyUnitId = () => {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_presence_control ||
            authenticatedUser.permissions.deac_manage_acting_body_units_presence_control
        ) {
            return vacancyActingBodyUnitId;
        }

        return authenticatedUser.actingBodyUnitId;
    };

    const getActingBodyIdForWorkplace = () => {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_presence_control) {
            return 0;
        }

        return authenticatedUser.actingBodyUnit.actingBodyId;
    };

    const getActingBodyUnitIdForWorkplace = () => {
        if (!authenticatedUser) {
            return 0;
        }

        if (authenticatedUser.permissions.deac_manage_all_acting_bodies_presence_control ||
            authenticatedUser.permissions.deac_manage_acting_body_units_presence_control
        ) {
            return 0;
        }
        return authenticatedUser.actingBodyUnitId;
    };

    if (!authenticatedUser) {
        return <></>;
    }

    return (
        <Filters onCleanFilters={handleCleanFilter} onFilters={handleFilter} onStatusChange={onStatusChange}>
            <FilterInput
                label={translate('typeToSearch')}
                value={textFilter}
                placeholder={translate('typeToSearch')}
                onChange={setTextFilter}
            />

            <WorkplacesDropdown
                actingBodyId={getActingBodyIdForWorkplace()}
                actingBodyUnitId={getActingBodyUnitIdForWorkplace()}
                setValue={setWorkplaceId}
                value={workplaceId}
                zIndex={8}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
                allowAllActingBodies={authenticatedUser.permissions.deac_manage_all_acting_bodies_presence_control}
            />

            <ActingBodiesDropdown
                label={translate('vacancyActingBody')}
                includeAllOption={true}
                value={getVacancyActingBodyId()}
                setValue={setVacancyActingBodyId}
                zIndex={7}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
                disabled={!authenticatedUser.permissions.deac_manage_all_acting_bodies_presence_control}
            />

            <ActingBodiesUnitsDropdown
                includeAllOption={true}
                label={translate('vacancyUnit')}
                value={getVacancyActingBodyUnitId()}
                setValue={setVacancyActingBodyUnitId}
                actingBodyId={getVacancyActingBodyId()}
                zIndex={6}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
                disabled={!(authenticatedUser.permissions.deac_manage_all_acting_bodies_presence_control ||
                    authenticatedUser.permissions.deac_manage_acting_body_units_presence_control
                )}
            />

            <ActingBodiesCommandDropdown
                label={translate('vacancyCommand')}
                value={vacancyActingBodyCommandId}
                setValue={setVacancyActingBodyCommandId}
                zIndex={5}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
                actingBodyId={getVacancyActingBodyId()}
                includeAllOption
            />

            {authenticatedUser.permissions.deac_manage_all_acting_bodies_presence_control &&
                <ActingBodiesDropdown
                    label={translate('agentActingBody')}
                    includeAllOption={true}
                    value={actingBodyId}
                    setValue={setActingBodyId}
                    zIndex={4}
                    labelStyle={styles.selectLabelStyle}
                    viewStyle={{ gap: 8 }}
                />
            }

            <ActingBodiesUnitsDropdown
                includeAllOption={true}
                label={translate('agentUnit')}
                value={actingBodyUnitId}
                setValue={setActingBodyUnitId}
                actingBodyId={getActingBodyId()}
                zIndex={3}
                labelStyle={styles.selectLabelStyle}
                viewStyle={{ gap: 8 }}
            />

            { type !== 'pending'
                ? <>
                    <UserDropdown
                        label={translate('registeredBy')}
                        actingBodyId={getActingBodyId()}
                        setValue={setRegisteredBy}
                        value={registeredBy}
                        zIndex={2}
                        labelStyle={styles.selectLabelStyle}
                        viewStyle={{ gap: 8 }}
                    />
                    <UserDropdown
                        label={translate('changedBy')}
                        actingBodyId={getActingBodyId()}
                        setValue={setChangedBy}
                        value={changedBy}
                        zIndex={1}
                        labelStyle={styles.selectLabelStyle}
                        viewStyle={{ gap: 8 }}
                    />
                </>
                : null
            }
        </Filters>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    selectLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    }
}));

export default Filter;
